import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RouterProvider } from 'react-router';
import { router } from './routes';
import { useAuthStore } from './store/useAuthStore';
import { useStore } from './store/useStore';
import { prepareTelegramWebApp } from './lib/telegram';

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: string | null }
> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error: error.message || 'Критическая ошибка рендера интерфейса.' };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen w-full bg-[#F5F5F7] flex items-center justify-center p-6">
          <div className="max-w-xl w-full bg-white border border-[#FFD3D3] rounded-[20px] p-6 shadow-sm">
            <h1 className="text-[22px] font-semibold text-[#A40000] mb-3">Ошибка интерфейса</h1>
            <p className="text-[14px] text-black/70 mb-5 break-words">{this.state.error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-[10px] bg-[#007AFF] text-white text-[14px] font-medium"
            >
              Перезагрузить
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  const initializeAuth = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isBootstrapping = useAuthStore((state) => state.isBootstrapping);
  const bootstrapData = useStore((state) => state.bootstrapData);
  const resetData = useStore((state) => state.resetData);
  const loadTickets = useStore((state) => state.loadTickets);
  const loadTicketDetails = useStore((state) => state.loadTicketDetails);
  const loadTemplates = useStore((state) => state.loadTemplates);
  const activeTicketId = useStore((state) => state.activeTicketId);
  const tickets = useStore((state) => state.tickets);
  const unreadTicketIds = useMemo(
    () =>
      tickets
        .filter((ticket) => !ticket.isRead && ticket.unreadCount > 0 && ticket.id !== activeTicketId)
        .map((ticket) => ticket.id),
    [activeTicketId, tickets],
  );
  const unreadIdsRef = useRef<Set<string>>(new Set());
  const [fatalError, setFatalError] = useState<string | null>(null);

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      setFatalError(event.error?.message || event.message || 'Критическая ошибка приложения.');
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      setFatalError(
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'Необработанная ошибка Promise.',
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    prepareTelegramWebApp();
    void initializeAuth().catch((error) => {
      setFatalError(error instanceof Error ? error.message : 'Не удалось инициализировать авторизацию.');
    });
  }, [initializeAuth]);

  useEffect(() => {
    if (isBootstrapping) {
      return;
    }

    if (isAuthenticated) {
      void bootstrapData();
      return;
    }

    resetData();
  }, [bootstrapData, isAuthenticated, isBootstrapping, resetData]);

  useEffect(() => {
    if (!isAuthenticated || isBootstrapping) {
      return;
    }

    const ticketsInterval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      void loadTickets();
    }, 15000);

    const detailsInterval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (!activeTicketId) {
        return;
      }
      void loadTicketDetails(activeTicketId).catch(() => undefined);
    }, 8000);

    return () => {
      window.clearInterval(ticketsInterval);
      window.clearInterval(detailsInterval);
    };
  }, [activeTicketId, isAuthenticated, isBootstrapping, loadTicketDetails, loadTickets]);

  useEffect(() => {
    if (!isAuthenticated || isBootstrapping) {
      return;
    }

    const apiBase = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8120';
    const stream = new EventSource(`${apiBase}/operator/stream`, { withCredentials: true });

    const handleTicketEvent = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as { ticketId?: string; customerId?: string };
        void loadTickets();

        if (!activeTicketId) {
          return;
        }

        const shouldRefreshActive =
          !payload.ticketId ||
          payload.ticketId === activeTicketId;
        if (shouldRefreshActive) {
          void loadTicketDetails(activeTicketId).catch(() => undefined);
        }
      } catch {
        void loadTickets();
      }
    };

    const handleCustomerEvent = () => {
      void loadTickets();
      if (activeTicketId) {
        void loadTicketDetails(activeTicketId).catch(() => undefined);
      }
    };

    const handleTemplateEvent = () => {
      void loadTemplates().catch(() => undefined);
    };

    stream.addEventListener('ticket-updated', handleTicketEvent as EventListener);
    stream.addEventListener('customer-updated', handleCustomerEvent as EventListener);
    stream.addEventListener('template-updated', handleTemplateEvent as EventListener);

    return () => {
      stream.removeEventListener('ticket-updated', handleTicketEvent as EventListener);
      stream.removeEventListener('customer-updated', handleCustomerEvent as EventListener);
      stream.removeEventListener('template-updated', handleTemplateEvent as EventListener);
      stream.close();
    };
  }, [
    activeTicketId,
    isAuthenticated,
    isBootstrapping,
    loadTicketDetails,
    loadTemplates,
    loadTickets,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      unreadIdsRef.current = new Set();
      return;
    }

    const currentUnreadSet = new Set(unreadTicketIds);
    const hasNewUnread = unreadTicketIds.some((ticketId) => !unreadIdsRef.current.has(ticketId));
    const inTelegramMiniApp = Boolean(window.Telegram?.WebApp);
    if (hasNewUnread && !inTelegramMiniApp && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Support Panel', {
        body: 'Появились новые сообщения или тикеты.',
      });
    }

    unreadIdsRef.current = currentUnreadSet;
  }, [isAuthenticated, unreadTicketIds]);

  if (fatalError) {
    return (
      <div className="min-h-screen w-full bg-[#F5F5F7] flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white border border-[#FFD3D3] rounded-[20px] p-6 shadow-sm">
          <h1 className="text-[22px] font-semibold text-[#A40000] mb-3">Ошибка интерфейса</h1>
          <p className="text-[14px] text-black/70 mb-3 break-words">{fatalError}</p>
          <p className="text-[13px] text-black/60 mb-5">
            Проверьте `VITE_API_BASE_URL`, `OPERATOR_ALLOWED_ORIGINS`, cookies/SameSite и откройте снова.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-[10px] bg-[#007AFF] text-white text-[14px] font-medium"
          >
            Перезагрузить
          </button>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
}
